import React, { lazy } from "react";
import { BC_HQ } from "app_constants";
import { MenuIcon } from "components/icons/MenuIcon";
import GroupManagementContentToolbar from "group_management/components/GroupManagementContentToolbar";
import { DateRangeSupport } from "helpers/shared/formatters/dates";

import { entityModeRoute } from "navigation/entity_mode_route";
import {
  groupManagementEntityVerifyPermissions,
  groupManagementVerifyPermissions,
} from "navigation/permissions";
import { LD_FEATURE_FLAGS } from "types/context";
import type { ISecondaryNavProps, ISectionProps } from "types/navigation";

// Every Menu Management surface lives under a SINGLE feature slug per entity,
// with list / create / edit distinguished by the trailing path — clean and
// bookmarkable, no "edit" smooshed into the slug:
//   /menu-management/display-groups              (list, in the sidebar)
//   /menu-management/display-groups/create       (create)
//   /menu-management/display-groups/edit/<key>   (edit)
// entityModeRoute() renders the right list/create/edit loader wrapper off the
// :tab segment; the wrapper supplies its own mode. All pages share the
// group-management LaunchDarkly flag (client SDK camelCased groupManagement)
// that gates the bulk endpoints server-side, and stay hidden — sidebar and deep
// links — until a brand is rolled out (groupManagement*VerifyPermissions).
type Loader = NonNullable<ISecondaryNavProps["component"]>;

function entityFeature(
  feature: string,
  slug: string,
  list: Loader,
  create: Loader,
  edit: Loader,
  verifyPermissions: ISecondaryNavProps["verifyPermissions"]
): ISecondaryNavProps {
  return {
    feature,
    app: BC_HQ,
    component: entityModeRoute(list, create, edit),
    // Each page renders its own heading through GroupManagementToolbarPortal,
    // which needs the bare toolbar as its portal host — the standard
    // ContentToolBar would nest a second titled, shadowed bar inside the first.
    contentToolbarComponent: GroupManagementContentToolbar,
    level: ["brand", "store", "group"],
    scope: ["staff", "superuser"],
    path: `/menu-management/${slug}`,
    dateSupport: DateRangeSupport.NONE,
    requiresFeatureFlag: LD_FEATURE_FLAGS.groupManagement,
    verifyPermissions,
  };
}

const MENU_MANAGEMENT: ISectionProps = {
  feature: "Menu Management",
  icon: <MenuIcon />,
  path: "/menu-management",
  level: ["brand", "store", "group"],
  dateSupport: DateRangeSupport.NONE,
  requiresFeatureFlag: LD_FEATURE_FLAGS.groupManagement,
  subSections: [
    entityFeature(
      "Menus",
      "menus",
      lazy(
        () =>
          import(
            "group_management/containers/menus_list/menus_list_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/menu_create/menu_create_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/menu_view/menu_view_loader_wrapper"
          )
      ),
      groupManagementVerifyPermissions
    ),
    entityFeature(
      "Items",
      "items",
      lazy(
        () =>
          import(
            "group_management/containers/items_list/items_list_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/item_create/item_create_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/item_edit/item_edit_loader_wrapper"
          )
      ),
      groupManagementVerifyPermissions
    ),
    entityFeature(
      "Display Groups",
      "display-groups",
      lazy(
        () =>
          import(
            "group_management/containers/display_groups/display_groups_list_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/display_groups/display_group_create_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/display_groups/display_group_edit_loader_wrapper"
          )
      ),
      groupManagementEntityVerifyPermissions
    ),
    entityFeature(
      "Modifiers",
      "modifiers",
      lazy(
        () =>
          import(
            "group_management/containers/modifiers/modifiers_list_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/modifiers/modifier_create_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/modifiers/modifier_edit_loader_wrapper"
          )
      ),
      groupManagementEntityVerifyPermissions
    ),
    entityFeature(
      "Modifier Groups",
      "modifier-groups",
      lazy(
        () =>
          import(
            "group_management/containers/modifier_groups/modifier_groups_list_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/modifier_groups/modifier_group_create_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/modifier_groups/modifier_group_edit_loader_wrapper"
          )
      ),
      groupManagementEntityVerifyPermissions
    ),
    entityFeature(
      "Categories",
      "categories",
      lazy(
        () =>
          import(
            "group_management/containers/categories/categories_list_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/categories/category_create_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/categories/category_edit_loader_wrapper"
          )
      ),
      groupManagementEntityVerifyPermissions
    ),
    entityFeature(
      "Courses",
      "courses",
      lazy(
        () =>
          import(
            "group_management/containers/courses/courses_list_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/courses/course_create_loader_wrapper"
          )
      ),
      lazy(
        () =>
          import(
            "group_management/containers/courses/course_edit_loader_wrapper"
          )
      ),
      groupManagementEntityVerifyPermissions
    ),
  ],
};

export default MENU_MANAGEMENT;
